import { ref, computed, onMounted, onUnmounted } from 'vue';
import Auth from '../api/auth';
import { useAccount } from 'dashboard/composables/useAccount';

const DEFAULT_IDLE_MINUTES = 10;

export function useInactivityLogout() {
  const { currentAccount } = useAccount();
  const showWarning = ref(false);
  const countdown = ref(60);

  // Admin-configurable via General Settings; falls back to the original
  // 10-minute default when the account hasn't set one.
  const idleMs = computed(
    () =>
      (currentAccount.value?.settings?.inactivity_logout_duration ||
        DEFAULT_IDLE_MINUTES) *
      60 *
      1000
  );
  // Never let the warning window exceed half the idle duration, so a short
  // admin-configured timeout (e.g. 1 minute) still leaves time to act.
  const warnBeforeMs = computed(() =>
    Math.min(60 * 1000, Math.floor(idleMs.value / 2))
  );

  let idleTimer = null;
  let warnTimer = null;
  let countdownInterval = null;

  function clearAllTimers() {
    clearTimeout(idleTimer);
    clearTimeout(warnTimer);
    clearInterval(countdownInterval);
  }

  async function doLogout() {
    clearAllTimers();
    showWarning.value = false;
    try {
      await Auth.logout();
    } finally {
      window.location.href = '/auth/sign_in';
    }
  }

  function startCountdown() {
    countdown.value = warnBeforeMs.value / 1000;
    countdownInterval = setInterval(() => {
      countdown.value -= 1;
      if (countdown.value <= 0) {
        clearInterval(countdownInterval);
        doLogout();
      }
    }, 1000);
  }

  function scheduleWarning() {
    warnTimer = setTimeout(() => {
      showWarning.value = true;
      startCountdown();
    }, idleMs.value - warnBeforeMs.value);
  }

  function scheduleLogout() {
    idleTimer = setTimeout(doLogout, idleMs.value);
  }

  function resetTimers() {
    clearAllTimers();
    showWarning.value = false;
    scheduleWarning();
    scheduleLogout();
  }

  function stayLoggedIn() {
    resetTimers();
  }

  const EVENTS = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];

  function onActivity() {
    if (!showWarning.value) {
      resetTimers();
    }
  }

  onMounted(() => {
    EVENTS.forEach(e =>
      window.addEventListener(e, onActivity, { passive: true })
    );
    resetTimers();
  });

  onUnmounted(() => {
    EVENTS.forEach(e => window.removeEventListener(e, onActivity));
    clearAllTimers();
  });

  return { showWarning, countdown, stayLoggedIn, doLogout };
}
